import {
  useMedicineDetailsLazyQuery,
  MedicineDetailResult,
  MedicineDetailsQueryVariables,
  DraftQuotationDetailAddCommand,
  useAddDraftQuotationDetailMutation,
  useWholesalesQuery,
  MatchType, // MatchTypeをインポートに追加
} from 'features/graphql';
import React, { useEffect, useState } from 'react';
import { Alert, Container, Table, Row, Col, Button } from 'react-bootstrap';
import { PaginationComponent } from 'components/Pagination';
import { WholesaleNameHeaderCells } from 'components/WholesaleNameHeaderCells';
import { MultilineText } from 'components/MultilineText';
import { graphQlErrorMessage, Paging } from 'utils/miscellaneous';
import { isDatePassed } from 'utils/dateUtils';
import { extractStopDates } from 'utils/stopDateUtils';
import { Loading } from 'components/Loading';
import { ChevronLeft, Plus } from 'react-bootstrap-icons';
import Form from 'react-bootstrap/Form';
import { Link, useNavigate } from 'react-router-dom';
import { DRAFT_QUOTATIONS_PATH } from 'route/paths';
import { AddMedicine } from './AddMedicine';

type MedicineType = '外' | '注' | '内' | '歯';

// stopExpectedDateから各種類の終了日を抽出して表示用の文字列を返す関数
export const MedicineList = () => {
  const [paging, setPaging] = useState<Paging>({ page: 0, pageSize: 10 });
  const [selectedMedicines, setSelectedMedicines] = useState<MedicineDetailResult[]>([]);
  const [medicineTypes, setMedicineTypes] = useState<MedicineType[]>(['外', '注', '内']);
  const [medicineName, setMedicineName] = useState('');
  const [capacity, setCapacity] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [genericName, setGenericName] = useState('');
  const [janCode, setJanCode] = useState('');
  const [yjCode, setYjCode] = useState('');
  const [gs1Code, setGs1Code] = useState('');
  const [medicineCategory, setMedicineCategory] = useState('');
  const [searchOperation, setSearchOperation] = useState('and');
  const [excludeProcessed, setExcludeProcessed] = useState(false);
  // selectedの型をMatchTypeに変更
  const [selected, setSelected] = useState<MatchType>(MatchType.Partial);

  const [showModal, setShowModal] = useState<
    'deleteModal' | 'addDetailModal' | 'deleteDetailModal' | null
  >(null);
  const navigate = useNavigate();

  const [medicineDetailsQuery, { data, loading: medicinesLoding, error }] =
    useMedicineDetailsLazyQuery();
  const [addDraftQuotationDetailMutation, { loading: addLoading }] =
    useAddDraftQuotationDetailMutation({
      onCompleted() {
        navigate(DRAFT_QUOTATIONS_PATH);
      },
    });

  // 卸売業者情報取得
  const { data: wholesalesData } = useWholesalesQuery();

  const executeSearch = (resetPagination: boolean = false) => {
    let searchPage = paging.page;

    if (resetPagination) {
      searchPage = 0;
      setPaging({ page: 0, pageSize: paging.pageSize });
    }

    const variables: MedicineDetailsQueryVariables = {
      page: Math.max(0, searchPage),
      pageSize: paging.pageSize,
      medicineTypes,
      matchType: selected,
      excludeProcessed,
      isForPriceDisplay: false,
    };
    if (medicineName) variables.medicineName = medicineName;
    if (capacity) variables.capacity = capacity;
    if (manufacturer) variables.salesManufacturer = manufacturer;
    if (genericName) variables.commonName = genericName;
    if (janCode) variables.janCode = janCode;
    if (yjCode) variables.yjCode = yjCode;
    if (gs1Code) variables.gs1Code = gs1Code;
    if (medicineCategory) variables.drugCategoryName = medicineCategory;
    if (searchOperation) variables.searchOperation = searchOperation;
    medicineDetailsQuery({ variables });
  };

  const handleSearch = () => {
    executeSearch(true);
  };

  useEffect(() => {
    executeSearch(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging, medicineTypes, selected]);

  const onAdd = (medicine: MedicineDetailResult, on: boolean) => {
    let newSelectedMedicines: MedicineDetailResult[];
    if (on) {
      newSelectedMedicines = selectedMedicines.concat(medicine);
    } else {
      newSelectedMedicines = selectedMedicines.filter((m) => m.medicineId !== medicine.medicineId);
    }
    setSelectedMedicines(newSelectedMedicines);
  };

  const onTypeChange = (medicineType: MedicineType, on: boolean) => {
    let newSelectedTypes: MedicineType[];
    if (on) {
      newSelectedTypes = [...medicineTypes, medicineType];
    } else {
      newSelectedTypes = medicineTypes.filter((t) => t !== medicineType);
    }
    setMedicineTypes(newSelectedTypes);
    setPaging({ page: 0, pageSize: paging.pageSize });
    setSelected(selected);
  };

  const handleAddMedicines = () => {
    const commands = selectedMedicines.map((medicine) => {
      const command: DraftQuotationDetailAddCommand = {
        medicineId: medicine.medicineId ?? undefined,
        medicineName: medicine.medicineName ?? '',
        janCode: medicine.janCode ?? '',
        salesManufacturer: medicine.salesManufacturer ?? '',
        capacity: medicine.capacity ?? '',
        yjCode: medicine.yjCode ?? undefined,
        gs1Code: medicine.gs1Code ?? '',
        medicineType: medicine.medicineType ?? undefined,
      };
      return command;
    });
    addDraftQuotationDetailMutation({ variables: { commands } }).then((res) => {});
  };

  return (
    <>
      <Container fluid className="mt-5 px-5 pb-4 sheet">
        <Row></Row>
        <Row className="mt-4">
          <Col>
            <div className="fs-3">商品を検索</div>
          </Col>
          <Col xs="auto">
            <Button
              variant="light"
              className="d-flex align-items-center gap-2 rounded-pill px-4 border"
              onClick={() => setShowModal('addDetailModal')}
            >
              <Plus size={18} />
              <span>検索結果にない場合はこちら</span>
            </Button>
          </Col>
        </Row>

        <Row className="mt-4">
          <Col>
            <div className="search p-3 bg-light rounded">
              <div className="row g-3 mb-3">
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="品名"
                    value={medicineName}
                    onChange={(e) => setMedicineName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="規格容量"
                    value={capacity}
                    onChange={(e) => setCapacity(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="販売元名"
                    value={manufacturer}
                    onChange={(e) => setManufacturer(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="一般名"
                    value={genericName}
                    onChange={(e) => setGenericName(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col-2 d-flex align-items-center">
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                    <Form.Check
                      type="radio"
                      id="search-operator-and"
                      name="searchOperator"
                      defaultChecked
                      onChange={() => setSearchOperation('and')}
                    />
                    <label htmlFor="search-operator-and" className="ms-2">
                      and
                    </label>
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', marginRight: '1em' }}>
                    <Form.Check
                      type="radio"
                      id="search-operator-or"
                      name="searchOperator"
                      onChange={() => setSearchOperation('or')}
                    />
                    <label htmlFor="search-operator-or" className="ms-2">
                      or
                    </label>
                  </div>
                </div>
              </div>

              <div className="row g-3 mb-3 align-items-center">
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="JANコード"
                    value={janCode}
                    onChange={(e) => setJanCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="YJコード"
                    value={yjCode}
                    onChange={(e) => setYjCode(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="GS1コード"
                    value={gs1Code}
                    onChange={(e) => setGs1Code(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col">
                  <Form.Control
                    type="text"
                    placeholder="薬効分類"
                    value={medicineCategory}
                    onChange={(e) => setMedicineCategory(e.target.value)}
                    onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
                    className="rounded-pill"
                  />
                </div>
                <div className="col-2">
                  <Button className="w-100 bg-success rounded-pill" onClick={handleSearch}>
                    検索実行
                  </Button>
                </div>
              </div>

              <div className="d-flex flex-wrap align-items-center mt-3">
                {/*<div className="d-flex align-items-center gap-2">*/}
                <Form.Check
                  className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                  inline
                  label="内"
                  onChange={(e) => {
                    onTypeChange('内', e.target.checked);
                  }}
                  defaultChecked
                />
                <Form.Check
                  className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                  inline
                  label="外"
                  onChange={(e) => {
                    onTypeChange('外', e.target.checked);
                  }}
                  defaultChecked
                />
                <Form.Check
                  className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                  inline
                  label="注"
                  onChange={(e) => {
                    onTypeChange('注', e.target.checked);
                  }}
                  defaultChecked
                />
                <Form.Check
                  className="me-5 d-flex align-items-center gap-2 d-flex-center-label"
                  inline
                  label="歯"
                  onChange={(e) => {
                    onTypeChange('歯', e.target.checked);
                  }}
                />
                <Form.Check
                  inline
                  id="exclude-processed"
                  label="経過措置を対象外にする"
                  checked={excludeProcessed}
                  onChange={(e) => setExcludeProcessed(e.target.checked)}
                  className="me-4"
                />
              </div>
            </div>
          </Col>
        </Row>

        <Row className="mt-2">
          <Col>{error && <Alert variant="danger">{graphQlErrorMessage(error)}</Alert>}</Col>
        </Row>

        <Row>
          <div style={{ overflowX: 'auto', width: '100%' }}>
            <Table className="table-borderless">
              <thead className="text-success border-bottom border-success border-3">
                <tr>
                  <th rowSpan={3}></th>
                  <th rowSpan={3} className="align-middle text-nowrap" style={{ width: '3rem' }}>
                    区分
                  </th>
                  <th>品名</th>
                  <WholesaleNameHeaderCells wholesales={wholesalesData?.wholesales} rowSpan={3} />
                  <th>後発</th>
                  {/*<th rowSpan={2} className="align-middle" style={{ width: "15rem" }}>*/}
                  {/*  販売元名*/}
                  {/*</th>*/}
                  <th rowSpan={3} className="align-middle text-nowrap" style={{ width: '10rem' }}>
                    販売開始予定日
                  </th>
                  <th rowSpan={3} className="align-middle text-nowrap" style={{ width: '10rem' }}>
                    販売終了日
                  </th>
                  {/*<th rowSpan={2} className="align-middle" style={{ width: "10rem" }}>*/}
                  {/*  JANコード*/}
                  {/*</th>*/}
                  <th style={{ width: '10rem' }}>JANコード</th>
                </tr>
                <tr>
                  <th>規格</th>
                  <th rowSpan={2} className="align-top">
                    薬価
                  </th>
                  <th>YJコード</th>
                </tr>
                <tr>
                  <th>販売元名</th>
                  <th>GS1コード</th>
                </tr>
              </thead>

              {data?.medicineDetails?.medicines?.map((medicine) => {
                // 各medicine項目ごとに、stopExpectedDateから日付情報を抽出
                const stopDates = extractStopDates(medicine.stopExpectedDate);

                // 各卸毎の価格がある場合をチェックする関数（1卸でも価格があれば true）
                const hasAnyPrice = () => {
                  if (!medicine.deals || medicine.deals.length === 0) return false;
                  return medicine.deals.some(
                    (deal) => deal?.packagingPrice !== null && deal?.packagingPrice !== undefined
                  );
                };

                // 行をグレーアウトする条件をチェック（経過措置期間終了または販売中止のみ）
                const isRowGreyedOut =
                  isDatePassed(stopDates.expirationDate) || isDatePassed(stopDates.salesStopDate);

                // チェックボックスを無効にする条件をチェック（グレーアウト条件 + 価格がある場合）
                const isCheckboxDisabled = isRowGreyedOut || hasAnyPrice();

                // 共通のセルスタイル - これを各セルに適用する
                const cellBackgroundColor = isRowGreyedOut ? '#d6d6d6' : 'white';

                return (
                  <tbody key={medicine.medicineId} className={isRowGreyedOut ? 'disabled-row' : ''}>
                    <tr>
                      <td
                        rowSpan={3}
                        className="align-middle text-nowrap"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        <Form.Check
                          onChange={(e) => onAdd(medicine, e.target.checked)}
                          disabled={isCheckboxDisabled}
                        />
                      </td>
                      <td
                        rowSpan={3}
                        className="align-middle text-nowrap"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        {medicine.medicineType}
                      </td>
                      <td
                        className="text-success text-nowrap"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        {medicine.medicineName}
                      </td>
                      {wholesalesData?.wholesales?.map((wholesale) => {
                        const deal = medicine.deals?.find(
                          (d) => d?.wholesaleId === wholesale.wholesaleId
                        );
                        return (
                          <td
                            rowSpan={3}
                            key={wholesale.wholesaleId}
                            className="align-middle"
                            style={{ backgroundColor: cellBackgroundColor }}
                          >
                            {deal && deal.packagingPrice != null
                              ? Number(deal.packagingPrice).toLocaleString()
                              : '-'}
                          </td>
                        );
                      })}
                      <td style={{ backgroundColor: cellBackgroundColor }}>
                        {medicine.generic === 1 ? '後' : ''}
                      </td>
                      {/*<td rowSpan={2} className="align-middle text-break">*/}
                      {/*  {medicine.salesManufacturer}*/}
                      {/*</td>*/}
                      {/*<td rowSpan={3} className="align-middle">*/}
                      {/*  {medicine.saleStartExpectedDate}*/}
                      {/*</td>*/}
                      <td
                        rowSpan={3}
                        className="align-middle pre-line text-nowrap"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        <MultilineText text={medicine.saleStartExpectedDate} />
                      </td>
                      <td
                        rowSpan={3}
                        className="align-middle pre-line text-nowrap"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        <MultilineText text={medicine.stopExpectedDate} />
                      </td>
                      {/*<td rowSpan={2} className="align-middle">*/}
                      {/*  {medicine.janCode}*/}
                      {/*</td>*/}
                      {/*<td>{medicine.yjCode}</td>*/}
                      <td className="align-middle" style={{ backgroundColor: cellBackgroundColor }}>
                        {medicine.janCode}
                      </td>
                    </tr>
                    <tr>
                      <td className="text-nowrap" style={{ backgroundColor: cellBackgroundColor }}>
                        {medicine.capacity}
                      </td>

                      <td rowSpan={2} style={{ backgroundColor: cellBackgroundColor }}>
                        {medicine.currentPrice?.toLocaleString(undefined, {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2,
                        })}
                      </td>

                      <td style={{ backgroundColor: cellBackgroundColor }}>{medicine.yjCode}</td>
                    </tr>
                    <tr className="border-bottom">
                      <td
                        className="align-middle text-break"
                        style={{ backgroundColor: cellBackgroundColor }}
                      >
                        {medicine.salesManufacturer}
                      </td>
                      <td style={{ backgroundColor: cellBackgroundColor }}>{medicine.gs1Code}</td>
                    </tr>
                  </tbody>
                );
              })}
              <tfoot className="border-top border-success border-3">
                <tr></tr>
              </tfoot>
            </Table>
          </div>
          <PaginationComponent
            total={data?.medicineDetails?.count ?? 0}
            itemsPerPage={paging.pageSize}
            currentPage={paging.page + 1}
            pageSize={paging.pageSize}
            onPageChange={(page) => {
              setPaging({ page: page - 1, pageSize: paging.pageSize });
              setSelectedMedicines([]);
            }}
            onPageSize={(pageSize) => {
              setPaging({ page: 0, pageSize: +pageSize });
              setSelectedMedicines([]);
            }}
          >
            <Button
              variant="success"
              className="btn-light-gray btn-lg rounded-pill py-3 px-5 me-3"
              disabled={selectedMedicines.length === 0}
              onClick={handleAddMedicines}
            >
              チェックした{selectedMedicines.length} 件を見積に追加
            </Button>
          </PaginationComponent>
        </Row>
      </Container>
      <Container>
        <Row style={{ marginTop: '3rem', marginBottom: '8rem' }}>
          <Col className="d-flex justify-content-center align-items-center">
            <Link to={DRAFT_QUOTATIONS_PATH} className="text-success text-decoration-none">
              <ChevronLeft className="me-3" />
              見積依頼に戻る
            </Link>
          </Col>
        </Row>
      </Container>

      <AddMedicine
        show={showModal === 'addDetailModal'}
        handleOk={() => {
          setShowModal(null);
          navigate(DRAFT_QUOTATIONS_PATH);
        }}
        handleCancel={() => setShowModal(null)}
      />

      {(medicinesLoding || addLoading) && <Loading />}
    </>
  );
};

export default MedicineList;
